/* Pipeline — kanban board for the wholesale lead lifecycle.
 *
 * Slimmed Jun 2026 by extracting the card body into
 * `components/pipeline/KanbanCard.jsx` and the confirm-delete dialog
 * into `components/pipeline/DeleteLeadModal.jsx`. This module now owns
 * just the board layout + drag/drop state machine + delete/stage
 * mutations.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtMoney, STAGE_COLOR } from "@/lib/api";
import { Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SendForSignatureModal from "@/components/SendForSignatureModal";
import QuickCallModal from "@/components/QuickCallModal";
import { needsFollowUp } from "@/lib/followUp";
import { useAuth } from "@/context/AuthContext";
import KanbanCard from "@/components/pipeline/KanbanCard";
import DeleteLeadModal from "@/components/pipeline/DeleteLeadModal";
import { celebrateDeal } from "@/components/DealWonModal";

const STAGES = ["New Lead", "Contacted", "Analyzed", "Hot — Buyer Interested", "Offer Sent", "Under Contract", "Closed", "Dead"];

// Per-stage primary action — the ONE thing the operator should do next on
// every card in that column. UI highlights this action; others are subdued.
//   "call"      → CallButton (Rec call)
//   "signature" → Send for Signature
//   "none"      → no emphasis (Closed/Dead)
const STAGE_PRIMARY_ACTION = {
  "New Lead": "call",
  "Contacted": "call",
  "Analyzed": "call",
  "Hot — Buyer Interested": "signature",
  "Offer Sent": "signature",
  "Under Contract": "signature",
  "Closed": "none",
  "Dead": "none",
};

// Stages where chasing a follow-up is meaningless — the deal is already won
// or buried, so we don't want a "needs follow-up" badge nagging on these.
const SKIP_FOLLOWUP_STAGES = new Set(["Closed", "Dead"]);

export default function Pipeline() {
  const nav = useNavigate();
  const { user } = useAuth();
  // "Pro · AI Lead Score" lock badge surfaces for Free/Basic users on
  // every Kanban card that hasn't been scored yet — single-tap upgrade hint.
  const isProOrAbove = ["pro", "team", "hustler"].includes(user?.tier) || user?.role === "admin";
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [signLead, setSignLead] = useState(null);
  const [callLead, setCallLead] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // lead pending delete-confirm
  const [deleting, setDeleting] = useState(false);

  const fetchLeads = useCallback(async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Permanently delete a lead. Optimistic UI: remove from the board first,
  // call DELETE /api/leads/{id}, restore on failure.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleting(true);
    const snapshot = leads;
    setLeads((arr) => arr.filter((l) => l.id !== target.id));
    try {
      await api.delete(`/leads/${target.id}`);
      toast.success(`Moved "${target.property_address || "lead"}" to Trash`);
      setDeleteTarget(null);
    } catch (e) {
      setLeads(snapshot);
      toast.error(e?.response?.data?.detail || "Couldn't delete that lead");
    } finally {
      setDeleting(false);
    }
  };

  // "Move to Dead instead" off-ramp — softer alternative that preserves the
  // lead + its history in the Dead stage column.
  const moveToDead = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleting(true);
    const snapshot = leads;
    setLeads((arr) => arr.map((l) => (l.id === target.id ? { ...l, stage: "Dead" } : l)));
    try {
      await api.patch(`/leads/${target.id}/stage`, { stage: "Dead" });
      toast.success("Moved to Dead — you can revive it later");
      setDeleteTarget(null);
    } catch (e) {
      setLeads(snapshot);
      toast.error(e?.response?.data?.detail || "Couldn't move to Dead");
    } finally {
      setDeleting(false);
    }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  // Auto-scroll the kanban container when a dragged card approaches its
  // left or right edge — fixes the bug where columns 6+ are unreachable
  // by drag because native HTML5 DnD doesn't scroll the container itself.
  const boardRef = useRef(null);
  const scrollRafRef = useRef(null);
  const onBoardDragOver = (e) => {
    const el = boardRef.current;
    if (!el || dragId == null) return;
    const rect = el.getBoundingClientRect();
    const edge = 100;  // px from each edge that triggers auto-scroll
    const speed = 18;  // px per RAF tick
    let dx = 0;
    if (e.clientX < rect.left + edge) dx = -speed;
    else if (e.clientX > rect.right - edge) dx = speed;
    if (dx === 0) {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      return;
    }
    if (scrollRafRef.current) return;  // already ticking
    const tick = () => {
      el.scrollLeft += dx;
      scrollRafRef.current = requestAnimationFrame(tick);
    };
    scrollRafRef.current = requestAnimationFrame(tick);
  };
  const stopAutoScroll = () => {
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  };
  useEffect(() => () => stopAutoScroll(), []);

  const onDragOver = (stage) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overStage !== stage) setOverStage(stage);
  };
  const onDrop = (stage) => async (e) => {
    e.preventDefault();
    setOverStage(null);
    stopAutoScroll();
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.stage === stage) return;
    setLeads((arr) => arr.map((l) => (l.id === lead.id ? { ...l, stage } : l)));
    try {
      await api.patch(`/leads/${lead.id}/stage`, { stage });
      // Fire the deal-won celebration for the FIRST transition into "Closed"
      // on any given lead — the modal handles UX (image + share). Guarded
      // against no-op self-drops by the `lead.stage === stage` early-return
      // above, so we never celebrate the same lead twice in one drag.
      if (stage === "Closed" && lead.stage !== "Closed") {
        celebrateDeal({
          leadId:    lead.id,
          address:   lead.address || lead.property_address,
          city:      lead.city,
          state:     lead.state,
          firstName: (user?.name || user?.email || "").split(/\s+/)[0] || "",
          userId:    user?.id,
        });
      }
    } catch {
      setLeads((arr) => arr.map((l) => (l.id === lead.id ? { ...l, stage: lead.stage } : l)));
    }
  };

  const byStage = (s) => leads.filter((l) => l.stage === s);

  return (
    <div data-testid="pipeline-page">
      <div className="topbar">
        <div>
          <div className="crumb">Pipeline</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Deal Pipeline</h1>
        </div>
        <div className="text-sm text-[var(--ink-2)]">
          Drag cards between stages to update.{" "}
          <span className="hidden sm:inline opacity-70">· Scroll horizontally to see all 8 stages →</span>
        </div>
      </div>

      <div
        className="kanban"
        ref={boardRef}
        onDragOver={onBoardDragOver}
        onDragEnd={stopAutoScroll}
        data-testid="kanban-board"
      >
        {STAGES.map((s) => {
          const items = byStage(s);
          const total = items.reduce((acc, l) => acc + (Number(l.estimated_arv) || 0), 0);
          const stale = SKIP_FOLLOWUP_STAGES.has(s)
            ? 0
            : items.filter(needsFollowUp).length;
          const colSlug = s.replace(/\s+/g, "-").toLowerCase();
          return (
            <div
              key={s}
              className={"kanban-col" + (overStage === s ? " drag-over" : "")}
              style={{ "--stage-color": STAGE_COLOR[s] }}
              onDragOver={onDragOver(s)}
              onDragLeave={() => setOverStage(null)}
              onDrop={onDrop(s)}
              data-testid={`kanban-col-${colSlug}`}
            >
              <div className="kanban-col-header">
                <div className="kanban-col-header-row">
                  <div className="kanban-col-title">
                    <span
                      className="chip-dot"
                      style={{ background: STAGE_COLOR[s], width: 10, height: 10, flexShrink: 0 }}
                    />
                    <span className="kanban-stage-label" title={s}>{s}</span>
                  </div>
                  <span
                    className="kanban-col-count"
                    data-testid={`kanban-col-count-${colSlug}`}
                  >
                    {items.length}
                  </span>
                </div>
                <div className="kanban-col-header-row">
                  <div className="kanban-col-amount">{fmtMoney(total)}</div>
                  {stale > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                      title={`${stale} lead${stale === 1 ? "" : "s"} in this stage need a touch (no call in 7+ days)`}
                      data-testid={`kanban-col-stale-${colSlug}`}
                    >
                      <Flame size={9} className="text-amber-700" />
                      {stale}
                    </span>
                  )}
                </div>
              </div>
              <div className="kanban-col-body">
                {items.length === 0 ? (
                  <div className="kanban-empty" data-testid={`kanban-empty-${colSlug}`}>
                    Drag a lead here
                  </div>
                ) : (
                  items.map((l) => (
                    <KanbanCard
                      key={l.id}
                      lead={l}
                      stage={s}
                      isDragging={dragId === l.id}
                      primaryAction={STAGE_PRIMARY_ACTION[s]}
                      isProOrAbove={isProOrAbove}
                      onDragStart={onDragStart(l.id)}
                      onDragEnd={() => setDragId(null)}
                      onOpen={() => nav(`/app/leads/${l.id}/report`)}
                      onDelete={() => setDeleteTarget(l)}
                      onCall={() => setCallLead(l)}
                      onSign={() => setSignLead(l)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <SendForSignatureModal
        open={!!signLead}
        onOpenChange={(o) => !o && setSignLead(null)}
        lead={signLead}
        onSent={() => { setSignLead(null); fetchLeads(); }}
      />
      <QuickCallModal
        open={!!callLead}
        lead={callLead}
        onClose={() => setCallLead(null)}
        onLogged={() => { setCallLead(null); fetchLeads(); }}
      />
      <DeleteLeadModal
        lead={deleteTarget}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        onMoveToDead={moveToDead}
      />
    </div>
  );
}
